import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import Layout from './Layout';
import './FreightRecap.css';
import './TradeRecap.css';
import withAuth from '../utils/withAuth';
import { Link } from 'react-router-dom';
import ChatWindow from './ChatWindow';
import { fetchHMSEntities } from '../services/hmsEntityService';
import VesselSearchAutocomplete from './VesselSearchAutocomplete';
// Multi-port itinerary + trade-recap tolerance parity (Aug 2026). Both
// controls are shared, not freight-local copies: the ports rows wrap the same
// PortsDropdown, and the tolerance pair is the trade recap's own control
// extracted rather than re-implemented for a fourth screen.
import PortItineraryInput from './shared/PortItineraryInput';
import ToleranceInput from './shared/ToleranceInput';
import {
  formatToleranceField,
  parseToleranceString,
} from '../utils/shippingTolerance';
import ErrorPopup from './ErrorPopup';
import { OfferContext } from '../OfferContext';
import TradeRecapBackModal from './TradeRecapBackModal';
import DateRangePicker from './ui/DateRangePicker';
import DatePicker from './ui/DatePicker';
import { useFlashMessage, DEFAULT_DURATION } from './FlashMessage';
import { debounce } from 'lodash';
import { getErrorMessage } from '../utils/errorMessages';
import AddressInput from './ui/AddressInput';
import AmountInput from './ui/AmountInput';
import { PRICE_DECIMALS, QUANTITY_DECIMALS } from '../utils/precision';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';
import { goToTradeOps, useTradeOpsDesk } from '../utils/tradeOpsDesk';
import RecapProductSearch from './RecapProductSearch';
// Sprint D — inline "+ Create product" flow when search has no hits
import InlineProductCreator from './Stock/InlineProductCreator';
import InlineCrmCreator from './CRM/InlineCrmCreator';
// Freight Review & Send — the same modal the Bunkers CC/SN dispatches use,
// pointed at the freight-recap dispatch endpoints via its ``api`` prop.
import DispatchReviewSendModal from './FuelEnquiries/DispatchReviewSendModal';
import RecapPartnerSearch from './RecapPartnerSearch';
import systemDataService from '../services/systemDataService';
import { buildRecapDisplayName, RECAP_NAME_PROFILES } from '../utils/recapDisplayName';
// Freight UI parity (Aug 2026): the freight recap now mounts the same
// shared screen kit the Trade Recap uses — .hms-form-screen header,
// sticky numbered stepper, Section/Field cards and the floating
// + Actions menu — so the two screens read as one design system.
import Section from './ui/Section';
import Field from './ui/Field';
import HMSStepper from './ui/HMSStepper';
import HMSFloatingActionMenu from './ui/HMSFloatingActionMenu';
import './ui/HMSScreenHeader.css';
import DealDocumentsPanel from './DealDocumentsPanel';
// Same relationship graph the Trade Ops list opens for a freight recap
// ("View Relationships" → documentType 'freight_recap'). It's a modal,
// not a route, so the recap screen mounts it directly.
import InvoiceRelationshipModal from './Invoicing/InvoiceRelationshipModal';
// Summary tiles reuse the Bunkers financial-panel cell styling verbatim
// (uppercase label + bold amount) rather than growing a second look.
import './TradeRecap/FuelsFinancialSummaryPanel.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faSave, faPaperPlane, faProjectDiagram,
} from '@fortawesome/free-solid-svg-icons';

// Sections of the freight form, in render order. The ids double as the
// ``hms-anchor-<id>`` Section anchors the stepper scrolls to.
const FREIGHT_SECTIONS = [
  { id: 'charter', label: 'Charter' },
  { id: 'pricing', label: 'Pricing & Terms' },
  { id: 'parties', label: 'Parties' },
  { id: 'banking', label: 'Banking' },
  { id: 'commissions', label: 'Commissions & Info' },
  { id: 'summary', label: 'Summary' },
];

// Summary-tile money formatter. Mirrors FuelsFinancialSummaryPanel's
// ``formatUSD`` but honours the recap's own currency; a missing value
// renders as an em-dash rather than a misleading 0.
const formatMoney = (value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return `${currency || 'USD'} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// Save-time shaping for the two list/pair fields, applied by BOTH the autosave
// and the manual-save cleaners so they can never drift:
//   - port itineraries go out as ordered arrays with the blank rows the
//     trader left behind stripped (an all-blank itinerary saves as []),
//   - the tolerance bounds go out as-is plus the combined display string the
//     backend keeps for back-compat, built by the shared formatter.
const shapeFreightListFields = (cleaned) => {
  ['load_ports', 'discharge_ports'].forEach((field) => {
    const list = Array.isArray(cleaned[field]) ? cleaned[field] : [];
    cleaned[field] = list
      .map((port) => (port === null || port === undefined ? '' : String(port).trim()))
      .filter((port) => port !== '');
  });

  cleaned.shipping_tolerance = formatToleranceField(
    cleaned.tolerance_lower_value,
    cleaned.tolerance_lower_unit,
    cleaned.tolerance_upper_value,
    cleaned.tolerance_upper_unit,
  );
  return cleaned;
};

function FreightRecap() {
  const { offerId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { offerResponse } = useContext(OfferContext);
  const [freightRecap, setFreightRecap] = useState(null);
  // Row 127: the desk is the loaded recap's own, else the Trade Ops table's
  // hint, else (Back only) the desk this person can open. The hint alone made
  // a fuels recap opened by link save back as Cargo.
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: freightRecap?.commodity_type,
    hintCommodity: location.state?.commodityType,
  });
  const commodityType = tradeOpsDesk.commodity;
  // Row 125: why an existing freight recap didn't load. While set, the screen
  // shows RecordLoadProblem INSTEAD of the form: this screen has no loading
  // gate, so a failed load used to leave an empty form whose Save created a
  // duplicate recap.
  const [recapLoadProblem, setRecapLoadProblem] = useState(null);
  const [recapLoadAttempt, setRecapLoadAttempt] = useState(0);
  const [hmsEntities, setHMSEntities] = useState([]);
  const { showError, showSuccess } = useFlashMessage();
  const [customRecapName, setCustomRecapName] = useState('');
  const [isEditingRecapName, setIsEditingRecapName] = useState(false);
  
  // Autosave state variables
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(Date.now());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  const [autoSavedNeedsConfirm, setAutoSavedNeedsConfirm] = useState(false);
  
  // Reference to store original state for potential rollback
  const [originalState, setOriginalState] = useState(null);
  const originalDataRef = useRef(null);
  const debouncedSaveRef = useRef(null);
  
  // Autosave configuration constants
  const AUTOSAVE_INTERVAL = 10000; // 10 seconds interval
  const AUTOSAVE_DEBOUNCE = 30000; // 30 seconds debounce

  // -- Review & Send (freight document dispatch) --------------------------
  // ``dispatchSummary`` mirrors the recap serializer's read-only
  // ``dispatch_summary`` (null until Review & Send is first opened);
  // ``dispatchRow`` is the full row the modal renders, fetched on demand.
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchRow, setDispatchRow] = useState(null);
  const [dispatchSummary, setDispatchSummary] = useState(null);
  const [dispatchLoading, setDispatchLoading] = useState(false);

  // The modal is source-agnostic: these four calls satisfy its ``api`` prop
  // but hit the freight-recap nested endpoints (creator-scoped,
  // HasTradeOpsAccess) instead of the bunkers /dispatches/ viewset. The
  // dispatch id argument is ignored — the recap id keys everything.
  const freightDispatchApi = useMemo(() => ({
    previewPdf: async () => {
      const response = await axiosInstance.get(
        `/api/offer/freight-recaps/${offerId}/dispatch/preview/`,
        { responseType: 'blob' },
      );
      return response.data;
    },
    editableFields: async () => {
      const response = await axiosInstance.get(
        `/api/offer/freight-recaps/${offerId}/dispatch/editable-fields/`,
      );
      return response.data;
    },
    saveOverrides: async (_dispatchId, overrides) => {
      const response = await axiosInstance.post(
        `/api/offer/freight-recaps/${offerId}/dispatch/save-overrides/`,
        { field_overrides: overrides || {} },
      );
      return response.data;
    },
    send: async (_dispatchId, recipients) => {
      const recipientEmails = Array.isArray(recipients)
        ? recipients
        : [recipients].filter(Boolean);
      const response = await axiosInstance.post(
        `/api/offer/freight-recaps/${offerId}/dispatch/send/`,
        { recipient_emails: recipientEmails },
      );
      return response.data;
    },
  }), [offerId]);

  const refreshDispatchRow = useCallback(async () => {
    const response = await axiosInstance.get(
      `/api/offer/freight-recaps/${offerId}/dispatch/`,
    );
    setDispatchRow(response.data);
    setDispatchSummary({
      id: response.data?.id,
      status: response.data?.status,
      sent_at: response.data?.sent_at,
      recipient_email: response.data?.recipient_email,
    });
    return response.data;
  }, [offerId]);

  // Open Review & Send: persist any unsaved form edits first (the PDF is
  // rendered from the SAVED recap, not the form state), then fetch/create
  // the dispatch row and open the modal.
  const handleOpenDispatch = async () => {
    if (dispatchLoading) return;
    setDispatchLoading(true);
    try {
      if (hasUnsavedChanges) {
        // Persist without leaving the screen — plain handleSave() navigates
        // back to the trades list, which would unmount us under the modal.
        await handleSave({ navigate: false });
      }
      await refreshDispatchRow();
      setDispatchModalOpen(true);
    } catch (error) {
      console.error('Failed to open Review & Send:', error);
      showError(getErrorMessage(error, 'Could not open Review & Send. Please try again.'));
    } finally {
      setDispatchLoading(false);
    }
  };

  // Local DealDocument rows for this recap (the dispatched PDF + any
  // API-side saves) — distinct from the DocuWare cabinet panel.
  const [savedDealDocs, setSavedDealDocs] = useState([]);
  const fetchSavedDealDocs = useCallback(async () => {
    if (!offerId) return;
    try {
      const resp = await axiosInstance.get('/api/offer/deal-documents/', {
        params: { freight_recap_id: offerId },
      });
      setSavedDealDocs(
        Array.isArray(resp.data) ? resp.data : (resp.data?.results || []),
      );
    } catch (error) {
      console.error('Failed to load saved deal documents:', error);
    }
  }, [offerId]);
  useEffect(() => { fetchSavedDealDocs(); }, [fetchSavedDealDocs]);

  const handleDispatchSent = () => {
    showSuccess('Freight recap sent. The document is filed under Legal ▸ Freight Contracts.', DEFAULT_DURATION);
    // Refresh the row so the header chip flips to Sent; best-effort.
    refreshDispatchRow().catch(() => {});
    fetchSavedDealDocs();
  };

  // Freight-specific form fields
  const [formFields, setFormFields] = useState({
    // Core charter details
    entity: '',
    charter_party_date: '',
    quantity_mt: '',
    quantity_unit: 'MT',
    // Quantity tolerance — trade-recap shape (lower/upper + unit each).
    tolerance_lower_value: '',
    tolerance_lower_unit: '%',
    tolerance_upper_value: '',
    tolerance_upper_unit: '%',
    product: '',
    stock_item_variant: null,
    vessel: '',
    laycan: '',
    laycan_comments: '',
    // Ordered itineraries — the recap's only stored port truth.
    load_ports: [],
    discharge_ports: [],
    freight_price: '',
    currency: 'USD',
    additional_price_info: '',
    payment_terms: '',
    demurrage_dispatch: '',

    // Parties
    charterer: '',
    charterer_crm_id: null,
    charterer_address: '',
    owners: '',
    owners_crm_id: null,
    disponent_owners: '',
    manager: '',
    freight_beneficiary_address: '',

    // Bank details
    account_owner: '',
    bank_address: '',
    bank_account_no: '',
    iban_no: '',
    swift_code: '',

    // Correspondent bank
    corr_account_owner: '',
    corr_bank_address: '',
    corr_bank_account_no: '',
    corr_iban_no: '',
    corr_swift_code: '',

    // Commissions
    address_commission_pct: '',
    mercurius_commission_pct: '',
    brokerage_commission_pct: '',

    additional_info: '',
    current_status: 'ACTIVE'
  });

  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const [units, setUnits] = useState([]);

  // Shared screen kit: which numbered Section the stepper highlights, and
  // the relationship graph the + Actions menu opens.
  const [activeStep, setActiveStep] = useState(FREIGHT_SECTIONS[0].id);
  const [relationshipModalOpen, setRelationshipModalOpen] = useState(false);

  const currencyOptions = ["USD", "EUR", "GBP", "ZAR", "CHF", "CZK", "SGD", "AED", "VND"];

  // Validation functions for number and percentage inputs
  const validatePercentage = (value) => {
    if (!value) return true;
    // Allow positive numbers with optional decimals for percentages
    return /^\d*\.?\d*$/.test(value) && (!value || parseFloat(value) >= 0);
  };

  const validatePrice = (value) => {
    if (!value) return true;
    // During typing, allow partial numeric input with decimals
    return /^\d*\.?\d*$/.test(value);
  };

  const formatPriceValue = (value) => {
    if (!value) return '';
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');
    if (cleanValue === '' || cleanValue === '.') return cleanValue;
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return '';
    // Format with 2 decimal places
    return numValue.toFixed(2);
  };


  // Add cleanup effect for body style
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Add isActive helper for navigation
  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const isDashboardActive = () => {
    return location.pathname.startsWith('/dashboard') ? "active" : "";
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    history.push('/login');
  };

  // Add scroll to top effect
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll-spy for the stepper — same rule the Trade Recap uses: the
  // active step is whichever Section anchor sits closest to just below
  // the sticky stepper bar (120px from the viewport top).
  useEffect(() => {
    const handler = () => {
      let bestId = FREIGHT_SECTIONS[0].id;
      let bestDist = Infinity;
      FREIGHT_SECTIONS.forEach(({ id }) => {
        const el = document.getElementById(`hms-anchor-${id}`);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const dist = top >= 120 ? top - 120 : 120 - top;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      });
      setActiveStep((curr) => (curr === bestId ? curr : bestId));
    };
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Fetch existing Freight Recap or initialize new one
  useEffect(() => {
    console.log('[DEBUG] useEffect triggered - fetchFreightRecap', { offerId, locationSearch: location.search });
    setRecapLoadProblem(null);

    const fetchFreightRecap = async () => {
      try {
        if (offerId === 'new') {
          // Handle new freight recap
          console.log('[DEBUG] Initializing new freight recap');
          setFormFields(prevFields => ({
            ...prevFields,
            current_status: 'ACTIVE'
          }));
          return;
        }

        if (!offerId) {
          // If no offerId is provided, we're creating a new recap
          console.log('[DEBUG] No offerId provided, initializing new freight recap');
          setFormFields(prevFields => ({
            ...prevFields,
            current_status: 'ACTIVE'
          }));
          return;
        }

        // Existing logic for fetching freight recap by ID
        console.log('[DEBUG] Fetching existing freight recap with ID:', offerId);
        const response = await axiosInstance.get(`/api/offer/freight-recaps/${offerId}/`);
        const existingRecap = response.data;
        
        console.log('[DEBUG] Loaded existing freight recap:', existingRecap);
        setFreightRecap(existingRecap);
        setCustomRecapName(existingRecap.recap_display_name || '');
        setDispatchSummary(existingRecap.dispatch_summary || null);
        setHasUnsavedChanges(true);
        
        // The bounds are the stored truth; the combined ``shipping_tolerance``
        // string only back-fills a row written before the split (same
        // fallback the trade recap uses).
        const toleranceParsed = parseToleranceString(
          existingRecap.shipping_tolerance);

        // Set form fields from existing data
        setFormFields({
          entity: existingRecap.entity || '',
          charter_party_date: existingRecap.charter_party_date || '',
          quantity_mt: existingRecap.quantity_mt || '',
          quantity_unit: existingRecap.quantity_unit || 'MT',
          tolerance_lower_value:
            existingRecap.tolerance_lower_value || toleranceParsed.lowerValue,
          tolerance_lower_unit:
            existingRecap.tolerance_lower_unit || toleranceParsed.lowerUnit,
          tolerance_upper_value:
            existingRecap.tolerance_upper_value || toleranceParsed.upperValue,
          tolerance_upper_unit:
            existingRecap.tolerance_upper_unit || toleranceParsed.upperUnit,
          product: existingRecap.product || '',
          stock_item_variant: existingRecap.stock_item_variant || null,
          vessel: existingRecap.vessel || '',
          laycan: existingRecap.laycan || '',
          laycan_comments: existingRecap.laycan_comments || '',
          load_ports: Array.isArray(existingRecap.load_ports)
            ? existingRecap.load_ports : [],
          discharge_ports: Array.isArray(existingRecap.discharge_ports)
            ? existingRecap.discharge_ports : [],
          freight_price: existingRecap.freight_price || '',
          currency: existingRecap.currency || 'USD',
          additional_price_info: existingRecap.additional_price_info || '',
          payment_terms: existingRecap.payment_terms || '',
          demurrage_dispatch: existingRecap.demurrage_dispatch || '',
          charterer: existingRecap.charterer || '',
          charterer_crm_id: existingRecap.charterer_crm_id || null,
          charterer_address: existingRecap.charterer_address || '',
          owners: existingRecap.owners || '',
          owners_crm_id: existingRecap.owners_crm_id || null,
          disponent_owners: existingRecap.disponent_owners || '',
          manager: existingRecap.manager || '',
          freight_beneficiary_address: existingRecap.freight_beneficiary_address || '',
          account_owner: existingRecap.account_owner || '',
          bank_address: existingRecap.bank_address || '',
          bank_account_no: existingRecap.bank_account_no || '',
          iban_no: existingRecap.iban_no || '',
          swift_code: existingRecap.swift_code || '',
          corr_account_owner: existingRecap.corr_account_owner || '',
          corr_bank_address: existingRecap.corr_bank_address || '',
          corr_bank_account_no: existingRecap.corr_bank_account_no || '',
          corr_iban_no: existingRecap.corr_iban_no || '',
          corr_swift_code: existingRecap.corr_swift_code || '',
          address_commission_pct: existingRecap.address_commission_pct || '',
          mercurius_commission_pct: existingRecap.mercurius_commission_pct || '',
          brokerage_commission_pct: existingRecap.brokerage_commission_pct || '',
          additional_info: existingRecap.additional_info || '',
          current_status: existingRecap.current_status || 'ACTIVE'
        });
      } catch (error) {
        console.error('Error fetching Freight Recap:', error);
        // Keep the answer: a refused, missing or half-loaded recap must not
        // fall through to the empty form.
        if (offerId && offerId !== 'new') {
          setRecapLoadProblem(describeLoadFailure(error));
        }
      }
    };

    fetchFreightRecap();
  }, [offerId, location.search, recapLoadAttempt]);

  // Load HMS Entities
  useEffect(() => {
    const loadHMSEntities = async () => {
      try {
        const entities = await fetchHMSEntities();
        console.log('Fetched HMS entities:', entities);
        setHMSEntities(entities);
      } catch (error) {
        console.error('Error fetching HMS entities:', error);
      }
    };

    loadHMSEntities();
  }, []);

  // Load quantity units from stock system
  useEffect(() => {
    const loadUnits = async () => {
      try {
        const unitsData = await systemDataService.getUnits();
        const activeUnits = unitsData.filter(unit => unit.is_active);
        setUnits(activeUnits);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };

    loadUnits();
  }, []);

  // Add useEffect to handle AI responses
  useEffect(() => {
    if (offerResponse && Object.keys(offerResponse).length > 0) {
      console.log('[AI] Processing freight recap AI response:', offerResponse);
      
      // Map AI response to form fields
      const aiMappedFields = {};
      
      // Check if currency should be updated - only when it's actually different or with pricing
      const shouldUpdateCurrency = offerResponse.currency && 
        (offerResponse.freight_price || // Has pricing info, so currency is relevant
         offerResponse.currency !== formFields.currency); // Currency is actually different from current
      console.log('[AI] Currency update decision:', {
        offerResponseCurrency: offerResponse.currency,
        currentFormCurrency: formFields.currency,
        hasFreightPrice: !!offerResponse.freight_price,
        shouldUpdate: shouldUpdateCurrency
      });
      
      // Core charter details mapping
      if (offerResponse.entity) aiMappedFields.entity = offerResponse.entity;
      if (offerResponse.charter_party_date) aiMappedFields.charter_party_date = offerResponse.charter_party_date;
      if (offerResponse.quantity_mt) aiMappedFields.quantity_mt = offerResponse.quantity_mt.toString();
      // AI extraction still yields ONE tolerance percentage and ONE port per
      // direction. Map them onto the real shapes — a symmetric bound pair and
      // single-entry itineraries — so AI fill keeps working and the trader
      // can then add the extra calls / the asymmetric bound by hand.
      if (offerResponse.tolerance_pct) {
        const pct = offerResponse.tolerance_pct.toString();
        aiMappedFields.tolerance_lower_value = pct;
        aiMappedFields.tolerance_lower_unit = '%';
        aiMappedFields.tolerance_upper_value = pct;
        aiMappedFields.tolerance_upper_unit = '%';
      }
      if (offerResponse.product) aiMappedFields.product = offerResponse.product;
      if (offerResponse.vessel) aiMappedFields.vessel = offerResponse.vessel;
      if (offerResponse.laycan) aiMappedFields.laycan = offerResponse.laycan;
      if (offerResponse.laycan_comments) aiMappedFields.laycan_comments = offerResponse.laycan_comments;
      if (offerResponse.load_port) aiMappedFields.load_ports = [offerResponse.load_port];
      if (offerResponse.discharge_port) aiMappedFields.discharge_ports = [offerResponse.discharge_port];
      if (offerResponse.freight_price) aiMappedFields.freight_price = offerResponse.freight_price.toString();
      if (shouldUpdateCurrency) aiMappedFields.currency = offerResponse.currency;
      if (offerResponse.additional_price_info) aiMappedFields.additional_price_info = offerResponse.additional_price_info;
      if (offerResponse.payment_terms) aiMappedFields.payment_terms = offerResponse.payment_terms;
      if (offerResponse.demurrage_dispatch) aiMappedFields.demurrage_dispatch = offerResponse.demurrage_dispatch;
      
      // Parties mapping
      if (offerResponse.charterer) aiMappedFields.charterer = offerResponse.charterer;
      if (offerResponse.charterer_address) aiMappedFields.charterer_address = offerResponse.charterer_address;
      if (offerResponse.owners) aiMappedFields.owners = offerResponse.owners;
      if (offerResponse.disponent_owners) aiMappedFields.disponent_owners = offerResponse.disponent_owners;
      if (offerResponse.manager) aiMappedFields.manager = offerResponse.manager;
      if (offerResponse.freight_beneficiary_address) aiMappedFields.freight_beneficiary_address = offerResponse.freight_beneficiary_address;
      
      // Bank details mapping
      if (offerResponse.account_owner) aiMappedFields.account_owner = offerResponse.account_owner;
      if (offerResponse.bank_address) aiMappedFields.bank_address = offerResponse.bank_address;
      if (offerResponse.bank_account_no) aiMappedFields.bank_account_no = offerResponse.bank_account_no;
      if (offerResponse.iban_no) aiMappedFields.iban_no = offerResponse.iban_no;
      if (offerResponse.swift_code) aiMappedFields.swift_code = offerResponse.swift_code;
      
      // Correspondent bank mapping
      if (offerResponse.corr_account_owner) aiMappedFields.corr_account_owner = offerResponse.corr_account_owner;
      if (offerResponse.corr_bank_address) aiMappedFields.corr_bank_address = offerResponse.corr_bank_address;
      if (offerResponse.corr_bank_account_no) aiMappedFields.corr_bank_account_no = offerResponse.corr_bank_account_no;
      if (offerResponse.corr_iban_no) aiMappedFields.corr_iban_no = offerResponse.corr_iban_no;
      if (offerResponse.corr_swift_code) aiMappedFields.corr_swift_code = offerResponse.corr_swift_code;
      
      // Commissions mapping
      if (offerResponse.address_commission_pct) aiMappedFields.address_commission_pct = offerResponse.address_commission_pct.toString();
      if (offerResponse.mercurius_commission_pct) aiMappedFields.mercurius_commission_pct = offerResponse.mercurius_commission_pct.toString();
      if (offerResponse.brokerage_commission_pct) aiMappedFields.brokerage_commission_pct = offerResponse.brokerage_commission_pct.toString();
      
      if (offerResponse.additional_info) aiMappedFields.additional_info = offerResponse.additional_info;
      
      // Update form fields with AI extracted data
      if (Object.keys(aiMappedFields).length > 0) {
        setFormFields(prevFields => ({
          ...prevFields,
          ...aiMappedFields
        }));
        
        setHasUnsavedChanges(true);
        console.log('[AI] Updated form fields with AI data:', aiMappedFields);
      }
    }
  }, [offerResponse]);

  // NavAPI vessel search on the Vessel field (same component as the
  // enquiry + delivery forms). Picking a result fills the canonical name;
  // typing commits as free text so vessels outside the AIS DB still save.
  // Freight recaps carry no IMO / vessel-type fields, so only the name is
  // populated.
  const handleVesselSelected = (vessel) => {
    if (!vessel || !vessel.ship_name) return;
    setFormFields((prev) => ({ ...prev, vessel: vessel.ship_name }));
    setHasUnsavedChanges(true);
  };

  const handleVesselNameTyped = (value) => {
    setFormFields((prev) => ({ ...prev, vessel: value }));
    setHasUnsavedChanges(true);
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    console.log('[FREIGHT_DEBUG] Field change:', { name, type, value, checked });

    // Handle validation for specific field types. quantity_mt / freight_price
    // render through <AmountInput> and the tolerance pair through
    // <ToleranceInput>; all are wired directly (they never hit this handler),
    // so only the commission percentage fields need the keystroke guard here.
    if (name === 'address_commission_pct' ||
        name === 'mercurius_commission_pct' || name === 'brokerage_commission_pct') {
      // Validate percentage fields
      if (!validatePercentage(value)) {
        showError("Please enter a valid percentage", DEFAULT_DURATION);
        return;
      }
    }

    setFormFields((prevFields) => ({
      ...prevFields,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    console.log('[AUTOSAVE] hasUnsavedChanges set to true');
    console.log('[FREIGHT_DEBUG] Autosave conditions check:', { 
      hasUnsavedChanges: true, 
      freightRecap: !!freightRecap, 
      offerId: offerId 
    });
  };

  // Function to save autosave to sessionStorage.
  // Audit 2026-05-17: switched from localStorage so freight-recap autosave
  // (which contains commercial deal data) cannot be exfiltrated by XSS on
  // another tab and dies with the tab close.
  const saveToLocalStorage = (data) => {
    try {
      const key = `autosave_freightrecap_${offerId || 'new'}`;
      const dataWithTimestamp = {
        timestamp: Date.now(),
        data: data
      };
      sessionStorage.setItem(key, JSON.stringify(dataWithTimestamp));
      console.log(`[AUTOSAVE] Saved to sessionStorage with key: ${key}`);
      setAutosaveStatus('localStorage');
    } catch (error) {
      console.error('[AUTOSAVE] Failed to save autosave to sessionStorage:', error);
      setAutosaveStatus('failed');
    }
  };

  // Function for server saving
  const saveToServer = async (data) => {
    try {
      // Clean the form data - convert empty strings to null for numeric fields
      const cleanFormData = (data) => {
        console.log('[FREIGHT_DEBUG] Raw form data before cleaning:', data);
        
        const cleaned = { ...data };
        
        // Handle date fields - ensure proper format or null
        const dateFields = ['charter_party_date'];
        dateFields.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined) {
            cleaned[field] = null;
            console.log(`[FREIGHT_DEBUG] Set ${field} to null (was empty)`);
          } else if (cleaned[field]) {
            // Ensure date is in YYYY-MM-DD format
            const dateValue = cleaned[field];
            if (typeof dateValue === 'string' && dateValue.length > 0) {
              // Check if it's already in correct format
              if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                console.log(`[FREIGHT_DEBUG] Invalid date format for ${field}: ${dateValue}`);
                // Try to parse and reformat
                const parsedDate = new Date(dateValue);
                if (!isNaN(parsedDate.getTime())) {
                  cleaned[field] = parsedDate.toISOString().split('T')[0];
                  console.log(`[FREIGHT_DEBUG] Reformatted ${field} to: ${cleaned[field]}`);
                } else {
                  cleaned[field] = null;
                  console.log(`[FREIGHT_DEBUG] Could not parse ${field}, set to null`);
                }
              } else {
                console.log(`[FREIGHT_DEBUG] Date ${field} already in correct format: ${dateValue}`);
              }
            }
          }
        });
        
        // Numeric fields that should be null instead of empty strings (except
        // commission fields). The tolerance is a value+unit CharField pair now
        // (trade-recap shape), so it is deliberately not in this list.
        const numericFieldsToNull = [
          'quantity_mt',
          'freight_price'
        ];
        
        numericFieldsToNull.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined) {
            cleaned[field] = null;
            console.log(`[FREIGHT_DEBUG] Set numeric field ${field} to null (was empty)`);
          } else if (cleaned[field] !== null) {
            // Ensure it's a valid number
            const numValue = parseFloat(cleaned[field]);
            if (isNaN(numValue)) {
              cleaned[field] = null;
              console.log(`[FREIGHT_DEBUG] Invalid number for ${field}, set to null`);
            } else {
              cleaned[field] = numValue;
              console.log(`[FREIGHT_DEBUG] Converted ${field} to number: ${numValue}`);
            }
          }
        });
        
        // Commission fields that should be 0 instead of null (they have default=0 in the model)
        const commissionFields = [
          'address_commission_pct',
          'mercurius_commission_pct', 
          'brokerage_commission_pct'
        ];
        
        commissionFields.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined || cleaned[field] === null) {
            cleaned[field] = 0;
            console.log(`[FREIGHT_DEBUG] Set commission field ${field} to 0 (was empty/null)`);
          } else {
            // Ensure it's a valid number
            const numValue = parseFloat(cleaned[field]);
            if (isNaN(numValue)) {
              cleaned[field] = 0;
              console.log(`[FREIGHT_DEBUG] Invalid number for commission ${field}, set to 0`);
            } else {
              cleaned[field] = numValue;
              console.log(`[FREIGHT_DEBUG] Converted commission ${field} to number: ${numValue}`);
            }
          }
        });
        
        shapeFreightListFields(cleaned);

        console.log('[FREIGHT_DEBUG] Cleaned form data:', cleaned);
        return cleaned;
      };

      const submissionData = {
        ...cleanFormData(data),
        commodity_type: commodityType,
        current_status: data.current_status || 'ACTIVE',
        recap_display_name: customRecapName || null,
        is_autosave: true
      };

      console.log("[AUTOSAVE] Submitting data to server:", submissionData);

      let response;
      if (freightRecap) {
        // Update existing recap
        console.log('[DEBUG] Updating existing freight recap with ID:', freightRecap.id);
        response = await axiosInstance.put(
          `/api/offer/freight-recaps/${freightRecap.id}/`,
          submissionData
        );
        // Note: No navigation for autosave - only show success message
        // showSuccess('Freight recap auto-saved successfully.', 2000);
      } else if (offerId && offerId !== 'new') {
        // Handle case for existing freight recap but no freightRecap yet
        response = await axiosInstance.put(
          `/api/offer/freight-recaps/${offerId}/`,
          submissionData
        );
      } else {
        // Avoid creating new recaps through autosave - just use localStorage
        throw new Error("Cannot autosave new recap to server");
      }
      
      console.log("[AUTOSAVE] Success response:", response.data);
      setAutosaveStatus('success');
      return response.data;
    } catch (error) {
      console.error("[AUTOSAVE] Error saving to server:", error);
      console.error("[AUTOSAVE] Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        stack: error.stack
      });
      
      // Log the form data that failed to save
      console.error("[AUTOSAVE] Failed autosave form data:", JSON.stringify(data, null, 2));
      
      setAutosaveStatus('failed');
      throw error;
    }
  };

  // Core autosave function
  const performAutosave = useCallback(async () => {
    console.log('[AUTOSAVE] Autosave check', { 
      hasUnsavedChanges, 
      isDataInDatabase: !!freightRecap, 
      dataId: offerId 
    });
    
    // Check conditions before proceeding
    if (!hasUnsavedChanges) {
      console.log('[AUTOSAVE] Skipping - No unsaved changes');
      return;
    }
    
    if (!freightRecap) {
      console.log('[AUTOSAVE] Skipping - Data not in database yet');
      // For new forms, save to sessionStorage (audit 2026-05-17 hardening).
      try {
        const emergencyBackup = {
          formData: formFields,
          additionalValues: {
            customRecapName
          },
          timestamp: Date.now()
        };
        sessionStorage.setItem('freightAutosave_new', JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Saved to sessionStorage for new form');
      } catch (error) {
        console.error('[AUTOSAVE] sessionStorage backup failed', error);
      }
      return;
    }
    
    if (!offerId) {
      console.log('[AUTOSAVE] Skipping - No data ID available');
      return;
    }
    
    // All conditions passed, proceed with autosave
    try {
      console.log('[AUTOSAVE] Starting autosave operation');
      
      // Attempt to save to server if online
      if (isOnline) {
        console.log('[AUTOSAVE] Saving changes to server');
        await saveToServer(formFields);
        
        // Update timestamp but DO NOT reset hasUnsavedChanges
        setLastSaveTime(Date.now());
        setAutoSavedNeedsConfirm(true);
        
        console.log('[AUTOSAVE] Completed successfully - content saved but still needs navigation confirmation');
      } else {
        // Save to localStorage if offline
        console.log('[AUTOSAVE] Offline, saving to localStorage');
        saveToLocalStorage(formFields);
      }
    } catch (error) {
      console.error('[AUTOSAVE] Failed:', error);
      // Store emergency backup in sessionStorage (audit 2026-05-17 hardening).
      try {
        const emergencyBackup = {
          formData: formFields,
          additionalValues: {
            customRecapName
          },
          timestamp: Date.now()
        };
        sessionStorage.setItem(`freightAutosave_${offerId || 'new'}`, JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Emergency backup saved to sessionStorage');
      } catch (backupError) {
        console.error('[AUTOSAVE] Failed to create emergency backup:', backupError);
      }
    }
  }, [hasUnsavedChanges, freightRecap, offerId, formFields, customRecapName, isOnline]);

  // Set up debounced save function
  useEffect(() => {
    debouncedSaveRef.current = debounce(performAutosave, AUTOSAVE_DEBOUNCE);
    
    // Cleanup
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [performAutosave, AUTOSAVE_DEBOUNCE]);

  // Add trigger effect for autosave when changes are detected
  useEffect(() => {
    if (hasUnsavedChanges && debouncedSaveRef.current) {
      console.log('[AUTOSAVE] Changes detected, triggering debounced save');
      debouncedSaveRef.current();
    }
  }, [hasUnsavedChanges]);

  // Add interval-based checking
  useEffect(() => {
    console.log('[AUTOSAVE] Setting up autosave interval');
    
    const intervalId = setInterval(() => {
      const timeSinceLastSave = Date.now() - lastSaveTime;
      console.log('[AUTOSAVE] Interval check', { 
        hasUnsavedChanges, 
        freightRecap: !!freightRecap, 
        offerId,
        timeSinceLastSave
      });
      
      if (hasUnsavedChanges && timeSinceLastSave >= AUTOSAVE_INTERVAL) {
        console.log('[AUTOSAVE] Interval-based autosave triggered');
        performAutosave();
      }
    }, 30000); // Check every 30 seconds
    
    return () => {
      console.log('[AUTOSAVE] Clearing autosave interval');
      clearInterval(intervalId);
    };
  }, [hasUnsavedChanges, lastSaveTime, offerId, performAutosave, AUTOSAVE_INTERVAL]);

  // Add beforeunload handler for navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Add online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      console.log('[AUTOSAVE] Network back online');
      setIsOnline(true);
      
      // Optionally attempt to sync any unsaved changes
      if (hasUnsavedChanges) {
        performAutosave();
      }
    };
    
    const handleOffline = () => {
      console.log('[AUTOSAVE] Network offline, will use localStorage');
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [hasUnsavedChanges, performAutosave]);

  // Handle save button click
  // ``options.navigate = false`` saves WITHOUT the navigate-back-to-list
  // behaviour — used by Review & Send, which must persist the form and stay
  // on this screen (the PDF renders from the SAVED recap). Button clicks
  // pass the click event here, which leaves ``navigate`` undefined → true.
  const handleSave = async (options) => {
    const navigateAfter = !(options && options.navigate === false);
    console.log('=== SAVE BUTTON CLICKED ==='); // Test log to see if function is called
    try {
      console.log('[FREIGHT_DEBUG] Starting save operation');
      console.log('[FREIGHT_DEBUG] Current form fields:', formFields);
      
      // Clean the form data - convert empty strings to null for numeric fields
      const cleanFormData = (data) => {
        console.log('[FREIGHT_DEBUG] Raw form data before cleaning:', data);
        
        const cleaned = { ...data };
        
        // Handle date fields - ensure proper format or null
        const dateFields = ['charter_party_date'];
        dateFields.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined) {
            cleaned[field] = null;
            console.log(`[FREIGHT_DEBUG] Set ${field} to null (was empty)`);
          } else if (cleaned[field]) {
            // Ensure date is in YYYY-MM-DD format
            const dateValue = cleaned[field];
            if (typeof dateValue === 'string' && dateValue.length > 0) {
              // Check if it's already in correct format
              if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                console.log(`[FREIGHT_DEBUG] Invalid date format for ${field}: ${dateValue}`);
                // Try to parse and reformat
                const parsedDate = new Date(dateValue);
                if (!isNaN(parsedDate.getTime())) {
                  cleaned[field] = parsedDate.toISOString().split('T')[0];
                  console.log(`[FREIGHT_DEBUG] Reformatted ${field} to: ${cleaned[field]}`);
                } else {
                  cleaned[field] = null;
                  console.log(`[FREIGHT_DEBUG] Could not parse ${field}, set to null`);
                }
              } else {
                console.log(`[FREIGHT_DEBUG] Date ${field} already in correct format: ${dateValue}`);
              }
            }
          }
        });
        
        // Numeric fields that should be null instead of empty strings (except
        // commission fields). The tolerance is a value+unit CharField pair now
        // (trade-recap shape), so it is deliberately not in this list.
        const numericFieldsToNull = [
          'quantity_mt',
          'freight_price'
        ];
        
        numericFieldsToNull.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined) {
            cleaned[field] = null;
            console.log(`[FREIGHT_DEBUG] Set numeric field ${field} to null (was empty)`);
          } else if (cleaned[field] !== null) {
            // Ensure it's a valid number
            const numValue = parseFloat(cleaned[field]);
            if (isNaN(numValue)) {
              cleaned[field] = null;
              console.log(`[FREIGHT_DEBUG] Invalid number for ${field}, set to null`);
            } else {
              cleaned[field] = numValue;
              console.log(`[FREIGHT_DEBUG] Converted ${field} to number: ${numValue}`);
            }
          }
        });
        
        // Commission fields that should be 0 instead of null (they have default=0 in the model)
        const commissionFields = [
          'address_commission_pct',
          'mercurius_commission_pct', 
          'brokerage_commission_pct'
        ];
        
        commissionFields.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined || cleaned[field] === null) {
            cleaned[field] = 0;
            console.log(`[FREIGHT_DEBUG] Set commission field ${field} to 0 (was empty/null)`);
          } else {
            // Ensure it's a valid number
            const numValue = parseFloat(cleaned[field]);
            if (isNaN(numValue)) {
              cleaned[field] = 0;
              console.log(`[FREIGHT_DEBUG] Invalid number for commission ${field}, set to 0`);
            } else {
              cleaned[field] = numValue;
              console.log(`[FREIGHT_DEBUG] Converted commission ${field} to number: ${numValue}`);
            }
          }
        });
        
        shapeFreightListFields(cleaned);

        console.log('[FREIGHT_DEBUG] Cleaned form data:', cleaned);
        return cleaned;
      };

      const submissionData = {
        ...cleanFormData(formFields),
        commodity_type: commodityType,
        current_status: formFields.current_status || 'ACTIVE',
        recap_display_name: customRecapName || null
      };

      console.log('[FREIGHT_DEBUG] Final submission data:', JSON.stringify(submissionData, null, 2));

      let response;
      if (freightRecap) {
        // Update existing recap
        console.log('[DEBUG] Updating existing freight recap with ID:', freightRecap.id);
        response = await axiosInstance.put(
          `/api/offer/freight-recaps/${freightRecap.id}/`,
          submissionData
        );
        showSuccess('Freight recap updated successfully.', 3000);
        setHasUnsavedChanges(false);

        if (navigateAfter) {
          // Navigate back to trade operations dashboard
          goToTradeOps(history, tradeOpsDesk, {
            selectedDate: location.state?.selectedDate,
            activeTab: location.state?.activeTab || 'recaps',
          });
        }
        return response.data;
      } else {
        // Create new recap -- only from the "new recap" screen. With a recap
        // number in the address the recap simply hasn't loaded, and posting
        // here would make a second, blank recap instead of saving that one.
        if (offerId && offerId !== 'new') {
          showError("This freight recap hasn't loaded, so it can't be saved. Reload the page and try again.", DEFAULT_DURATION);
          return false;
        }
        // Create new recap
        console.log('[DEBUG] Creating new freight recap');
        console.log('[DEBUG] Submission URL:', '/api/offer/freight-recaps/');
        console.log('[DEBUG] Cleaned submission data:', JSON.stringify(submissionData, null, 2));
        
        try {
          response = await axiosInstance.post(
            '/api/offer/freight-recaps/',
            submissionData
          );
          console.log('[DEBUG] Success response:', response.data);
          
          // Immediately clear state to prevent any interference
          setHasUnsavedChanges(false);
          setAutoSavedNeedsConfirm(false);
          setLastSaveTime(Date.now());
          
          // Clear any sessionStorage backup since we've saved successfully.
          // Also clear any leftover localStorage entries from before the
          // 2026-05-17 audit hardening migration.
          const key = `autosave_freightrecap_${offerId || 'new'}`;
          sessionStorage.removeItem(key);
          sessionStorage.removeItem(`freightAutosave_${offerId || 'new'}`);
          localStorage.removeItem(key);
          localStorage.removeItem(`freightAutosave_${offerId || 'new'}`);

          showSuccess('New freight recap created successfully.', 3000);
          
          // Cancel any pending autosave callbacks before navigating away
          if (debouncedSaveRef.current && debouncedSaveRef.current.cancel) {
            debouncedSaveRef.current.cancel();
            console.log('[DEBUG] Pending debounced autosave cancelled after manual save');
          }
          
          // Navigate back to Trade Operations dashboard
          console.log('[DEBUG] Navigating to', tradeOpsDesk.basePath);
          goToTradeOps(history, tradeOpsDesk, {
            selectedDate: location.state?.selectedDate,
            activeTab: location.state?.activeTab || 'recaps',
          });
          
          return response.data;
        } catch (apiError) {
          console.error('[FREIGHT_DEBUG] API Error Details:', {
            status: apiError.response?.status,
            statusText: apiError.response?.statusText,
            data: apiError.response?.data,
            headers: apiError.response?.headers,
            config: {
              url: apiError.config?.url,
              method: apiError.config?.method,
              data: apiError.config?.data
            },
            message: apiError.message,
            stack: apiError.stack
          });
          
          // Log the exact request that failed
          console.error('[FREIGHT_DEBUG] Failed request data:', JSON.stringify(submissionData, null, 2));
          
          showError(getErrorMessage(apiError, 'Failed to save freight recap. Please try again.'), DEFAULT_DURATION);
          throw apiError; // Re-throw to be caught by outer catch
        }
      }
      
      // Clear unsaved changes flag for update case
      setHasUnsavedChanges(false);
      setAutoSavedNeedsConfirm(false);
      setLastSaveTime(Date.now());
      
      // Clear any sessionStorage backup since we've saved successfully.
      // Also clear stale localStorage entries from before audit 2026-05-17.
      const key = `autosave_freightrecap_${offerId || 'new'}`;
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`freightAutosave_${offerId || 'new'}`);
      localStorage.removeItem(key);
      localStorage.removeItem(`freightAutosave_${offerId || 'new'}`);

      return response.data;
    } catch (error) {
      console.error('Error saving freight recap:', error);
      showError(getErrorMessage(error, 'Failed to save freight recap. Please try again.'), DEFAULT_DURATION);
      return false;
    }
  };

  // Handle back button with confirmation
  const handleBackClick = () => {
    console.log('[DEBUG] Back button clicked. hasUnsavedChanges:', hasUnsavedChanges, 'autoSavedNeedsConfirm:', autoSavedNeedsConfirm);
    
    // Show confirmation if either we have unsaved changes OR content was autosaved but needs confirmation
    if (hasUnsavedChanges || autoSavedNeedsConfirm) {
      console.log('[DEBUG] Setting showBackConfirmation to true');
      setShowBackConfirmation(true);
    } else {
      console.log('[DEBUG] No unsaved changes or autosaved content, navigating directly');
      // If no unsaved changes, go back directly and restore previous state
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      });
    }
  };

  const handleConfirmBack = async () => {
    try {
      // Cancel any pending autosave
      if (debouncedSaveRef.current && debouncedSaveRef.current.cancel) {
        debouncedSaveRef.current.cancel();
        console.log('[AUTOSAVE] Canceled pending autosave on confirm back');
      }
      
      setShowBackConfirmation(false);
      setAutoSavedNeedsConfirm(false);
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      });
    } catch (error) {
      console.error('[AUTOSAVE] Error handling back button:', error);
      setShowBackConfirmation(false);
      setAutoSavedNeedsConfirm(false);
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      });
    }
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
  };

  const handleDraftAndLeave = async () => {
    setShowBackConfirmation(false);
    await handleSave();
  };

  // Sprint D — InlineProductCreator state + helpers. handleRecapProductChange
  // is shared by RecapProductSearch's onChange and InlineProductCreator's
  // onProductCreated so the same auto-fill runs for both paths.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
  });
  const handleRecapProductChange = (variantId, displayName, variant) => {
    const productName = variant?.product_type_display || (displayName || '').split('|')[0]?.trim() || displayName || '';
    const updates = { stock_item_variant: variantId, product: productName };
    if (variant) {
      if (variant.quantity) updates.quantity_mt = variant.quantity;
      if (variant.unit_code) updates.quantity_unit = variant.unit_code;
      if (variant.price_currency) updates.currency = variant.price_currency;
    }
    setFormFields(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };
  const handleInlineProductCreated = (variant) => {
    if (variant) {
      handleRecapProductChange(variant.id, variant.display_name || '', variant);
    }
    setProductCreatorState({ isOpen: false, searchTerm: '' });
  };

  // Inline CRM partner creator — gated to NEW recaps only (offerId === 'new'
  // or no offerId), so editing an existing freight recap can't mutate the
  // CRM catalog. Charterer defaults to "Buyer (Debtor)" and Owners default
  // to "Ship Owners" when seeding the modal's partner_type select.
  const isNewRecap = !offerId || offerId === 'new';
  const [crmCreatorState, setCrmCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
    targetFieldName: null,
    targetFieldNameId: null,
    role: 'Buyer (Debtor)',
    title: 'Create CRM Partner',
  });
  const [pendingCreatedPartners, setPendingCreatedPartners] = useState([]);
  const openCrmCreator = (targetFieldName, targetFieldNameId, role, title, searchTerm) => {
    setCrmCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      targetFieldName,
      targetFieldNameId,
      role: role || 'Buyer (Debtor)',
      title: title || 'Create CRM Partner',
    });
  };
  const closeCrmCreator = () => {
    setCrmCreatorState({
      isOpen: false,
      searchTerm: '',
      targetFieldName: null,
      targetFieldNameId: null,
      role: 'Buyer (Debtor)',
      title: 'Create CRM Partner',
    });
  };
  const handleInlineCrmPartnerCreated = (profile) => {
    if (!profile) {
      closeCrmCreator();
      return;
    }
    setPendingCreatedPartners((prev) => {
      if (prev.some((p) => p.id === profile.id)) return prev;
      return [...prev, profile];
    });
    const { targetFieldName, targetFieldNameId } = crmCreatorState;
    if (targetFieldName && targetFieldNameId) {
      setFormFields((prev) => ({
        ...prev,
        [targetFieldName]: profile.legal_entity_name,
        [targetFieldNameId]: profile.id,
      }));
      setHasUnsavedChanges(true);
    }
    closeCrmCreator();
  };

  // Freight cost summary tiles. The authority for these figures is the
  // backend (``FreightRecap.total_freight_cost`` / ``commission_amounts``
  // / ``net_freight``, served read-only on the recap payload) — that's
  // what the PDF and any later deal-economics rollup read. Here we mirror
  // the same arithmetic off live form state so the tiles move while the
  // trader types, and fall back to the saved server values whenever
  // quantity or rate is blank in the form.
  const freightSummary = useMemo(() => {
    const qty = toNumberOrNull(formFields.quantity_mt);
    const rate = toNumberOrNull(formFields.freight_price);

    if (qty === null || rate === null) {
      const commissions = freightRecap?.commission_amounts || {};
      return {
        total: toNumberOrNull(freightRecap?.total_freight_cost),
        address: toNumberOrNull(commissions.address),
        mercurius: toNumberOrNull(commissions.mercurius),
        brokerage: toNumberOrNull(commissions.brokerage),
        net: toNumberOrNull(freightRecap?.net_freight),
      };
    }

    const total = qty * rate;
    const commissionOf = (pct) => {
      const parsed = toNumberOrNull(pct);
      return total * (parsed === null ? 0 : parsed) / 100;
    };
    const address = commissionOf(formFields.address_commission_pct);
    const mercurius = commissionOf(formFields.mercurius_commission_pct);
    const brokerage = commissionOf(formFields.brokerage_commission_pct);
    return {
      total,
      address,
      mercurius,
      brokerage,
      net: total - address - mercurius - brokerage,
    };
  }, [
    formFields.quantity_mt,
    formFields.freight_price,
    formFields.address_commission_pct,
    formFields.mercurius_commission_pct,
    formFields.brokerage_commission_pct,
    freightRecap,
  ]);

  // Only non-zero commissions earn a tile — an untouched recap shouldn't
  // show three empty 0.00 cells.
  const summaryTiles = [
    { key: 'total', label: 'Total Freight Cost', value: freightSummary.total, strong: true },
    { key: 'address', label: 'Address Commission', value: freightSummary.address },
    { key: 'mercurius', label: 'Mercurius Commission', value: freightSummary.mercurius },
    { key: 'brokerage', label: 'Brokerage Commission', value: freightSummary.brokerage },
    { key: 'net', label: 'Net Freight', value: freightSummary.net, strong: true },
  ].filter((tile) => (
    tile.key === 'total' || tile.key === 'net' || (tile.value !== null && tile.value !== 0)
  ));

  // Actions that used to sit as a loose button row in the header. Save &
  // Workflow shares handleSave with Save Draft (unchanged behaviour — the
  // freight recap has no separate workflow step yet).
  const actionMenuItems = [
    {
      key: 'save-draft',
      label: 'Save Draft',
      icon: faSave,
      color: 'blue',
      onClick: () => handleSave(),
      title: 'Save the current freight recap state',
    },
    {
      key: 'save-workflow',
      label: 'Save & Workflow',
      icon: faPaperPlane,
      color: 'green',
      onClick: () => handleSave(),
      title: 'Save the freight recap and return to Trade Operations',
    },
    {
      key: 'review-send',
      label: dispatchLoading
        ? 'Opening…'
        : (dispatchSummary?.status === 'sent' ? 'View Sent Doc' : 'Review & Send'),
      icon: faPaperPlane,
      color: 'orange',
      hidden: !offerId,
      disabled: dispatchLoading,
      onClick: handleOpenDispatch,
      title: dispatchSummary?.status === 'sent'
        ? 'Already sent — review the dispatched document'
        : 'Review, edit and email the Freight Recap document',
    },
    {
      key: 'view-relationships',
      label: 'View Relationships',
      icon: faProjectDiagram,
      color: 'purple',
      hidden: !freightRecap?.id,
      onClick: () => setRelationshipModalOpen(true),
      title: 'Show this freight recap in the document relationship graph',
    },
  ];

  // Header name from the one shared namer (utils/recapDisplayName.js): the
  // freight profile reads Owners (else Charterer) · qty · product · laycan
  // span, so this header no longer shows a bare "Freight Recap" label.
  const recapDisplayName = buildRecapDisplayName(formFields, {
    profile: RECAP_NAME_PROFILES.FREIGHT,
    customName: customRecapName,
  });

  const renderRecapNameDisplay = () => (
    isEditingRecapName ? (
      <input
        type="text"
        value={customRecapName}
        onChange={(e) => setCustomRecapName(e.target.value)}
        className="hms-header-name-input"
        placeholder="Custom freight recap name..."
        autoFocus
        onBlur={() => setIsEditingRecapName(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsEditingRecapName(false);
          }
        }}
      />
    ) : (
      <span
        className="hms-header-name-display"
        onClick={() => setIsEditingRecapName(true)}
      >
        {recapDisplayName}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="hms-header-edit-icon"
        >
          <path d="M14.06 9.02L14.98 9.94L5.92 19H5V18.08L14.06 9.02ZM17.66 3C17.41 3 17.15 3.1 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C18.17 3.09 17.92 3 17.66 3ZM14.06 6.19L3 17.25V21H6.75L17.81 9.94L14.06 6.19Z"
          fill="currentColor"/>
        </svg>
      </span>
    )
  );

  if (offerId && offerId !== 'new' && recapLoadProblem) {
    return (
      <Layout
        showLeftNavSidebar={true}
        showRightSidebar={false}
        chatHeaderText="HAMSTIR Freight Recap Builder"
        isTradeRecap={true}
        recapType="Freight"
      >
        <RecordLoadProblem
          problem={recapLoadProblem}
          recordName="freight recap"
          areaName="freight recaps"
          backLabel="Back to Trade Ops"
          onBack={() => goToTradeOps(history, tradeOpsDesk, {
            selectedDate: location.state?.selectedDate,
            activeTab: location.state?.activeTab || 'recaps',
          })}
          onRetry={() => setRecapLoadAttempt((n) => n + 1)}
        />
      </Layout>
    );
  }

  return (
    <Layout
      showLeftNavSidebar={true}
      showRightSidebar={false}
      chatHeaderText="HAMSTIR Freight Recap Builder"
      isTradeRecap={true}
      recapType="Freight"
    >
      <div className="freight-recap hms-form-screen">
        {/* HMS Screen Header — shared .hms-screen-header chrome (glass
            back button, white logo, badge strip). The loose action-button
            row it used to carry now lives in the floating + Actions menu
            rendered near the end of this tree. */}
        <div className="hms-screen-header">
          <button
            className="hms-back-circle"
            onClick={handleBackClick}
            title="Back to Trade Operations"
            type="button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="hms-header-logo">
            <img src={logo} alt="IMT" className="hms-header-logo-img" />
            <div className="hms-header-title-content">
              <div className="hms-header-name-row">
                {renderRecapNameDisplay()}
              </div>
              <div className="hms-header-info-row">
                <span className="hms-stage-badge">Freight Recap</span>
                {freightRecap?.transaction_number && (
                  <span className="hms-recap-no-badge">
                    Recap no. {freightRecap.transaction_number}
                  </span>
                )}
                {dispatchSummary?.status === 'sent' && (
                  <span
                    className="freight-dispatch-sent-chip"
                    title={`Sent to ${dispatchSummary.recipient_email || '—'}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '4px 10px', borderRadius: '999px',
                      background: '#e6f6ec', color: '#1c7c3d',
                      fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                    }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    Sent{dispatchSummary.sent_at ? ` · ${new Date(dispatchSummary.sent_at).toLocaleDateString()}` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="hms-header-actions" />
        </div>

        <DispatchReviewSendModal
          isOpen={dispatchModalOpen}
          dispatch={dispatchRow}
          api={freightDispatchApi}
          onClose={() => setDispatchModalOpen(false)}
          onSent={handleDispatchSent}
        />

        <div className="hms-stepper-bar">
          <HMSStepper
            sections={FREIGHT_SECTIONS}
            activeId={activeStep}
            onStepClick={(id) => {
              setActiveStep(id);
              const el = document.getElementById(`hms-anchor-${id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </div>

        <div className="trade-recap-main-content">

          {/* ============================================== */}
          {/* 1 — CHARTER DETAILS                            */}
          {/* ============================================== */}
          <Section title="Charter Details" stepNumber={1} anchorId="hms-anchor-charter">
            <div className="hms-grid hms-grid-2">
              <Field label="Entity">
                <select
                  name="entity"
                  value={formFields.entity}
                  onChange={handleChange}
                  className="trade-recap-select"
                >
                  <option value="">Select Entity</option>
                  {hmsEntities && hmsEntities.length > 0 ? hmsEntities.map((entity, index) => {
                    const value = typeof entity === 'object' ?
                                (entity.value || entity.hms_entity_name || '') :
                                entity;
                    const displayText = typeof entity === 'object' ?
                                      (entity.label || entity.hms_entity_name || '') :
                                      entity;
                    return (
                      <option key={index} value={value}>
                        {displayText}
                      </option>
                    );
                  }) : <option value="" disabled>No entities available</option>}
                </select>
              </Field>

              <Field label="Charter Party Date">
                <DatePicker
                  name="charter_party_date"
                  value={formFields.charter_party_date || ''}
                  onChange={handleChange}
                  className="trade-recap-date"
                  format="YYYY-MM-DD"
                  placeholder="Select charter party date"
                />
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Product">
                <RecapProductSearch
                  label=""
                  fieldName="stock_item_variant"
                  displayFieldName="product"
                  value={formFields.product}
                  variantId={formFields.stock_item_variant}
                  onChange={handleRecapProductChange}
                  onCreateProduct={(searchTerm) => setProductCreatorState({ isOpen: true, searchTerm })}
                />
              </Field>

              <Field label="Quantity" hint="Drives the freight cost summary together with the freight price.">
                <div className="trade-recap-paired-fields">
                  <AmountInput
                    name="quantity_mt"
                    maxDecimals={QUANTITY_DECIMALS}
                    value={formFields.quantity_mt}
                    onChange={(value) => {
                      setFormFields(prev => ({
                        ...prev,
                        quantity_mt: value
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="0.00"
                  />
                  <select
                    name="quantity_unit"
                    value={formFields.quantity_unit || ''}
                    onChange={(e) => {
                      setFormFields(prev => ({
                        ...prev,
                        quantity_unit: e.target.value
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    className="trade-recap-select"
                    style={{ minWidth: '120px', flex: 1 }}
                  >
                    <option value="">Unit</option>
                    {units.map(unit => (
                      <option key={unit.id} value={unit.code}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Vessel">
                <VesselSearchAutocomplete
                  initialValue={formFields.vessel || ''}
                  onSelect={handleVesselSelected}
                  onQueryChange={handleVesselNameTyped}
                  placeholder="Search vessel by Name, IMO, MMSI or Call Sign..."
                />
              </Field>

              <Field label="Tolerance" hint="Lower and upper bound; may be asymmetric.">
                <ToleranceInput
                  namePrefix="tolerance"
                  values={{
                    lowerValue: formFields.tolerance_lower_value,
                    lowerUnit: formFields.tolerance_lower_unit,
                    upperValue: formFields.tolerance_upper_value,
                    upperUnit: formFields.tolerance_upper_unit,
                  }}
                  onChange={(patch) => {
                    const keyMap = {
                      lowerValue: 'tolerance_lower_value',
                      lowerUnit: 'tolerance_lower_unit',
                      upperValue: 'tolerance_upper_value',
                      upperUnit: 'tolerance_upper_unit',
                    };
                    setFormFields(prev => {
                      const next = { ...prev };
                      Object.entries(patch).forEach(([key, value]) => {
                        next[keyMap[key]] = value;
                      });
                      return next;
                    });
                    setHasUnsavedChanges(true);
                  }}
                  onInvalid={() => showError(
                    'Please enter a valid number for the tolerance', DEFAULT_DURATION)}
                />
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Load Port(s)" hint="In call order — add a row per load port.">
                <PortItineraryInput
                  ports={formFields.load_ports}
                  label="load port"
                  placeholder="Enter load port"
                  onChange={(ports) => {
                    setFormFields(prev => ({
                      ...prev,
                      load_ports: ports
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
              </Field>

              <Field label="Discharge Port(s)" hint="In call order — add a row per discharge port.">
                <PortItineraryInput
                  ports={formFields.discharge_ports}
                  label="discharge port"
                  placeholder="Enter discharge port"
                  onChange={(ports) => {
                    setFormFields(prev => ({
                      ...prev,
                      discharge_ports: ports
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Laycan">
                <DateRangePicker
                  name="laycan"
                  value={formFields.laycan || ''}
                  onChange={handleChange}
                  className="trade-recap-date-range"
                  format="YYYY-MM-DD"
                  placeholder="Select laycan date range"
                  style={{ width: '100%' }}
                />
              </Field>

              <Field label="Laycan Comments">
                <textarea
                  name="laycan_comments"
                  value={formFields.laycan_comments}
                  onChange={handleChange}
                  placeholder="Enter laycan comments"
                  rows="3"
                  className="trade-recap-textarea"
                />
              </Field>
            </div>
          </Section>

          {/* ============================================== */}
          {/* 2 — PRICING & TERMS                            */}
          {/* ============================================== */}
          <Section title="Pricing & Terms" stepNumber={2} anchorId="hms-anchor-pricing">
            <div className="hms-grid hms-grid-2">
              <Field label="Freight Price" hint="Per unit of the quantity above.">
                <AmountInput
                  name="freight_price"
                  maxDecimals={PRICE_DECIMALS}
                  value={formFields.freight_price}
                  onChange={(value) => {
                    setFormFields(prev => ({
                      ...prev,
                      freight_price: value
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Enter freight price"
                />
              </Field>

              <Field label="Currency">
                <select
                  name="currency"
                  value={formFields.currency}
                  onChange={handleChange}
                  className="trade-recap-select"
                >
                  {currencyOptions.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Payment Terms">
                <textarea
                  name="payment_terms"
                  value={formFields.payment_terms}
                  onChange={handleChange}
                  placeholder="Enter payment terms"
                  rows="3"
                  className="trade-recap-textarea"
                />
              </Field>

              <Field label="Additional Price Info">
                <textarea
                  name="additional_price_info"
                  value={formFields.additional_price_info}
                  onChange={handleChange}
                  placeholder="Enter additional price information"
                  rows="3"
                  className="trade-recap-textarea"
                />
              </Field>
            </div>

            <Field label="Demurrage / Dispatch">
              <textarea
                name="demurrage_dispatch"
                value={formFields.demurrage_dispatch}
                onChange={handleChange}
                placeholder="Enter demurrage and dispatch terms"
                rows="3"
                className="trade-recap-textarea"
              />
            </Field>
          </Section>

          {/* ============================================== */}
          {/* 3 — PARTIES & ADDRESSES                        */}
          {/* ============================================== */}
          <Section title="Parties & Addresses" stepNumber={3} anchorId="hms-anchor-parties">
            <div className="hms-grid hms-grid-2">
              <Field label="Charterer">
                <RecapPartnerSearch
                  label=""
                  fieldName="charterer"
                  fieldNameId="charterer_crm_id"
                  value={formFields.charterer}
                  crmId={formFields.charterer_crm_id}
                  onChange={(partnerId, partnerName) => {
                    setFormFields(prev => ({
                      ...prev,
                      charterer: partnerName,
                      charterer_crm_id: partnerId
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  onCreatePartner={isNewRecap ? (searchTerm) =>
                    openCrmCreator('charterer', 'charterer_crm_id', 'Charterer', 'Create Charterer (CRM)', searchTerm)
                    : undefined}
                  extraVisibleProfiles={pendingCreatedPartners}
                />
              </Field>

              <Field label="Owners">
                <RecapPartnerSearch
                  label=""
                  fieldName="owners"
                  fieldNameId="owners_crm_id"
                  value={formFields.owners}
                  crmId={formFields.owners_crm_id}
                  onChange={(partnerId, partnerName) => {
                    setFormFields(prev => ({
                      ...prev,
                      owners: partnerName,
                      owners_crm_id: partnerId
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  onCreatePartner={isNewRecap ? (searchTerm) =>
                    openCrmCreator('owners', 'owners_crm_id', 'Ship Owners', 'Create Owners (CRM)', searchTerm)
                    : undefined}
                  extraVisibleProfiles={pendingCreatedPartners}
                />
              </Field>
            </div>

            <div className="hms-grid hms-grid-2">
              <Field label="Manager">
                <input
                  type="text"
                  name="manager"
                  value={formFields.manager}
                  onChange={handleChange}
                  placeholder="Enter manager name"
                />
              </Field>

              <Field label="Disponent Owners">
                <input
                  type="text"
                  name="disponent_owners"
                  value={formFields.disponent_owners}
                  onChange={handleChange}
                  placeholder="Enter disponent owners"
                />
              </Field>
            </div>

            <Field label="Freight Beneficiary Address">
              <AddressInput
                name="freight_beneficiary_address"
                value={formFields.freight_beneficiary_address}
                onChange={handleChange}
                placeholder="Enter freight beneficiary address"
                rows={3}
              />
            </Field>
          </Section>

          {/* ============================================== */}
          {/* 4 — BANKING (beneficiary + correspondent)      */}
          {/* ============================================== */}
          <Section title="Banking" stepNumber={4} anchorId="hms-anchor-banking">
            <Field label="Freight Beneficiary Bank Account" hint="Where the freight is paid.">
              <input
                type="text"
                name="account_owner"
                value={formFields.account_owner}
                onChange={handleChange}
                placeholder="Enter account owner name"
              />
            </Field>

            <Field label="Bank Address">
              <AddressInput
                name="bank_address"
                value={formFields.bank_address}
                onChange={handleChange}
                placeholder="Enter bank address"
                rows={3}
              />
            </Field>

            <div className="hms-grid hms-grid-3">
              <Field label="Bank Account No.">
                <input
                  type="text"
                  name="bank_account_no"
                  value={formFields.bank_account_no}
                  onChange={handleChange}
                  placeholder="Enter bank account number"
                />
              </Field>

              <Field label="IBAN No.">
                <input
                  type="text"
                  name="iban_no"
                  value={formFields.iban_no}
                  onChange={handleChange}
                  placeholder="Enter IBAN number"
                />
              </Field>

              <Field label="SWIFT Code">
                <input
                  type="text"
                  name="swift_code"
                  value={formFields.swift_code}
                  onChange={handleChange}
                  placeholder="Enter SWIFT code"
                />
              </Field>
            </div>

            <Field label="Correspondent Bank Account Owner" hint="Intermediary bank, when the beneficiary bank needs one.">
              <input
                type="text"
                name="corr_account_owner"
                value={formFields.corr_account_owner}
                onChange={handleChange}
                placeholder="Enter correspondent account owner"
              />
            </Field>

            <Field label="Correspondent Bank Address">
              <AddressInput
                name="corr_bank_address"
                value={formFields.corr_bank_address}
                onChange={handleChange}
                placeholder="Enter correspondent bank address"
                rows={3}
              />
            </Field>

            <div className="hms-grid hms-grid-3">
              <Field label="Correspondent Bank Account No.">
                <input
                  type="text"
                  name="corr_bank_account_no"
                  value={formFields.corr_bank_account_no}
                  onChange={handleChange}
                  placeholder="Enter correspondent bank account number"
                />
              </Field>

              <Field label="Correspondent IBAN No.">
                <input
                  type="text"
                  name="corr_iban_no"
                  value={formFields.corr_iban_no}
                  onChange={handleChange}
                  placeholder="Enter correspondent IBAN number"
                />
              </Field>

              <Field label="Correspondent SWIFT Code">
                <input
                  type="text"
                  name="corr_swift_code"
                  value={formFields.corr_swift_code}
                  onChange={handleChange}
                  placeholder="Enter correspondent SWIFT code"
                />
              </Field>
            </div>
          </Section>

          {/* ============================================== */}
          {/* 5 — COMMISSIONS & ADDITIONAL INFO              */}
          {/* ============================================== */}
          <Section title="Commissions & Additional Information" stepNumber={5} anchorId="hms-anchor-commissions">
            <div className="hms-grid hms-grid-3">
              <Field label="Address Commission (%)" hint="Deducted from the freight cost in the summary.">
                <input
                  type="text"
                  name="address_commission_pct"
                  value={formFields.address_commission_pct}
                  onChange={handleChange}
                  placeholder="Enter address commission percentage"
                  inputMode="decimal"
                />
              </Field>

              <Field label="Mercurius Commission (%)">
                <input
                  type="text"
                  name="mercurius_commission_pct"
                  value={formFields.mercurius_commission_pct}
                  onChange={handleChange}
                  placeholder="Enter mercurius commission percentage"
                  inputMode="decimal"
                />
              </Field>

              <Field label="Brokerage Commission (%)">
                <input
                  type="text"
                  name="brokerage_commission_pct"
                  value={formFields.brokerage_commission_pct}
                  onChange={handleChange}
                  placeholder="Enter brokerage commission percentage"
                  inputMode="decimal"
                />
              </Field>
            </div>

            <Field label="Additional Information">
              <textarea
                name="additional_info"
                value={formFields.additional_info}
                onChange={handleChange}
                placeholder="Enter any additional information"
                rows="6"
                className="trade-recap-textarea"
              />
            </Field>
          </Section>

          {/* ============================================== */}
          {/* 6 — DOCUMENTS & SUMMARY                        */}
          {/* ============================================== */}
          <Section title="Documents & Summary" stepNumber={6} anchorId="hms-anchor-summary">
            <div className="ffsp-grid ffsp-grid--two-col">
              {summaryTiles.map((tile) => (
                <div key={tile.key} className="ffsp-cell">
                  <span className="ffsp-label">{tile.label}</span>
                  <span
                    className={[
                      'ffsp-value',
                      tile.strong ? 'ffsp-value--strong' : '',
                      (typeof tile.value === 'number' && tile.value < 0) ? 'ffsp-value--neg' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {formatMoney(tile.value, formFields.currency)}
                  </span>
                </div>
              ))}
            </div>

            {/* Saved documents live in the LOCAL DealDocument table — the
                dispatched Freight Recap PDF lands there on send — while the
                panel below lists the DocuWare deal cabinet. Both surfaces
                are needed: DocuWare never sees the locally-generated PDF. */}
            {freightRecap?.id && savedDealDocs.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <h4 className="hms-field-label" style={{ marginBottom: '6px' }}>Saved Documents</h4>
                {savedDealDocs.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '8px 12px', border: '1px solid #d0d5dd',
                      borderRadius: '8px', background: '#f8fafc',
                      marginBottom: '6px', fontSize: '13px',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#003B5C' }}>{doc.file_name}</span>
                    {doc.deal_document_type && <span>{doc.deal_document_type}</span>}
                    {doc.uploaded_at && (
                      <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    )}
                    {doc.download_url && (
                      <a
                        href={doc.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 'auto', fontWeight: 600 }}
                      >
                        Open
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Documents only exist once the recap has an id — the sent
                Freight Recap PDF files itself against this anchor, and
                manual uploads need something to hang off. */}
            {freightRecap?.id ? (
              <DealDocumentsPanel
                freightRecapId={freightRecap.id}
                title="Freight Recap Documents"
                employeeId={parseInt(localStorage.getItem('user_id'), 10) || null}
                partnerName={formFields.charterer || formFields.owners || ''}
              />
            ) : (
              <div className="hms-field-hint">
                Save the freight recap once to attach documents.
              </div>
            )}
          </Section>

        </div>

        {/* HMS Footer */}
        <Footer />
      </div>

      {/* Freight recap actions, replacing the old header button row. */}
      <HMSFloatingActionMenu label="Actions" items={actionMenuItems} />

      {/* Same graph Trade Ops opens from the recap row's Actions menu. */}
      <InvoiceRelationshipModal
        isOpen={relationshipModalOpen}
        onClose={() => setRelationshipModalOpen(false)}
        document={freightRecap}
        documentType="freight_recap"
      />

      {showBackConfirmation && (
        <TradeRecapBackModal
          message="Unsaved freight recap data will be lost, are you sure you want to go back?"
          onConfirm={handleConfirmBack}
          onCancel={handleCancelBack}
          onDraft={handleDraftAndLeave}
        />
      )}
      
      {/* Hamster Chat Integration */}
      <HamsterFloatingButton />
      
      {/* Back to Top Button */}
      <BackToTopButton />

      {/* Sprint D — Inline product creator. Freight recap is logistics-side
          (no buy/sell leg), so we default to purchase_price pre-fill. */}
      {productCreatorState.isOpen && (
        <InlineProductCreator
          isOpen={true}
          onClose={() => setProductCreatorState({ isOpen: false, searchTerm: '' })}
          onProductCreated={handleInlineProductCreated}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField="purchase_price"
          initialUnitPrice={''}
          initialCurrency={formFields.currency || 'USD'}
          initialQuantity={formFields.quantity_mt || ''}
        />
      )}

      {/* Inline CRM partner creator — only available while authoring a
          NEW freight recap. Pending stubs are surfaced on both Charterer
          and Owners pickers via pendingCreatedPartners. */}
      <InlineCrmCreator
        isOpen={crmCreatorState.isOpen}
        onClose={closeCrmCreator}
        onPartnerCreated={handleInlineCrmPartnerCreated}
        initialLegalEntityName={crmCreatorState.searchTerm}
        initialPartnerType={crmCreatorState.role || 'Buyer (Debtor)'}
        title={crmCreatorState.title || 'Create CRM Partner'}
      />
    </Layout>
  );
}

export default withAuth(FreightRecap); 